// @ts-nocheck
import React, { useEffect, useState } from 'react';

const inputStyle = {
  padding: '7px 9px',
  borderRadius: 8,
  border: '1px solid #dbe4f0',
  fontSize: 12,
  width: '100%',
  boxSizing: 'border-box',
};

const emptyForm = { first_name: '', last_name: '', email: '', relationship_type: 'parent', password: '' };

// Lets admins/teachers add, edit, or remove any number of parent/guardian
// accounts linked to a student, backed by per-role parent-link API endpoints.
export default function FamilyMembersEditor({ studentId, fetchParents, addParent, updateParent, removeParent, onChanged }: any) {
  const [parents, setParents] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');

  const [adding, setAdding] = useState<any>(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addError, setAddError] = useState<any>('');
  const [saving, setSaving] = useState<any>(false);

  const [editingId, setEditingId] = useState<any>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editError, setEditError] = useState<any>('');

  const [removingId, setRemovingId] = useState<any>(null);

  const load = () => {
    setLoading(true);
    setError('');
    fetchParents(studentId)
      .then((data) => setParents(data.parents || []))
      .catch(() => setError('Could not load family/guardian info.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleAdd = async () => {
    if (!addForm.first_name.trim() || !addForm.last_name.trim() || !addForm.email.trim()) {
      setAddError('First name, last name, and email are required.');
      return;
    }
    setSaving(true);
    setAddError('');
    try {
      await addParent(studentId, addForm);
      setAddForm(emptyForm);
      setAdding(false);
      load();
      onChanged?.();
    } catch (e: any) {
      setAddError(e.message || 'Failed to add family/guardian.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (member) => {
    setEditingId(member.parent_id);
    setEditForm({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      relationship_type: member.relationship_type || 'parent',
      password: '',
    });
    setEditError('');
  };

  const handleSaveEdit = async (parentId) => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()) {
      setEditError('First name, last name, and email are required.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await updateParent(studentId, parentId, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        relationship_type: editForm.relationship_type,
      });
      setEditingId(null);
      load();
      onChanged?.();
    } catch (e: any) {
      setEditError(e.message || 'Failed to update family/guardian.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member) => {
    const label = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'this family member/guardian';
    if (!window.confirm(`Remove ${label} from this student's account? This does not delete their user account.`)) {
      return;
    }
    setRemovingId(member.parent_id);
    try {
      await removeParent(studentId, member.parent_id);
      load();
      onChanged?.();
    } catch (e: any) {
      setError(e.message || 'Failed to remove family/guardian.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ color: '#1e3a5f', fontSize: 12 }}>Family Members / Guardians {parents.length ? `(${parents.length})` : ''}</strong>
        <button
          type="button"
          onClick={() => { setAdding((v) => !v); setAddError(''); setAddForm(emptyForm); }}
          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >
          {adding ? 'Cancel' : '+ Add Family Member'}
        </button>
      </div>

      {loading && <div style={{ fontSize: 12, color: '#64748b' }}>Loading...</div>}
      {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}

      {!loading && !parents.length && !adding && (
        <div style={{ fontSize: 12, color: '#64748b' }}>No family members/guardians linked yet.</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        {parents.map((member) => (
          <div key={member.parent_id} style={{ padding: '10px 12px', borderRadius: 10, background: '#ffffff', border: '1px solid #dbe4f0', fontSize: 11, color: '#475569', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.05)' }}>
            {editingId === member.parent_id ? (
              <div style={{ display: 'grid', gap: 6 }}>
                <input style={inputStyle} placeholder="First name" value={editForm.first_name} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} />
                <input style={inputStyle} placeholder="Last name" value={editForm.last_name} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} />
                <input style={inputStyle} placeholder="Email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                <input style={inputStyle} placeholder="Relationship (e.g. mother, father, guardian)" value={editForm.relationship_type} onChange={(e) => setEditForm((f) => ({ ...f, relationship_type: e.target.value }))} />
                {editError && <div style={{ color: '#dc2626' }}>{editError}</div>}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" disabled={saving} onClick={() => handleSaveEdit(member.parent_id)} style={{ flex: 1, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '6px 8px', fontWeight: 700, cursor: 'pointer' }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" disabled={saving} onClick={() => setEditingId(null)} style={{ flex: 1, background: '#f1f5f9', border: '1px solid #d1d9e6', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ lineHeight: 1.45, fontWeight: 700, color: '#1e3a5f' }}>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Family Member'}</div>
                <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.relationship_type || 'parent'}</div>
                <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.email || 'No email listed'}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button type="button" onClick={() => startEdit(member)} style={{ background: '#f1f5f9', border: '1px solid #d1d9e6', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={removingId === member.parent_id}
                    onClick={() => handleRemove(member)}
                    style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}
                  >
                    {removingId === member.parent_id ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <div style={{ padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px dashed #cbd5e1', display: 'grid', gap: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
            <input style={inputStyle} placeholder="First name" value={addForm.first_name} onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))} />
            <input style={inputStyle} placeholder="Last name" value={addForm.last_name} onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))} />
            <input style={inputStyle} placeholder="Email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} />
            <input style={inputStyle} placeholder="Relationship (e.g. mother)" value={addForm.relationship_type} onChange={(e) => setAddForm((f) => ({ ...f, relationship_type: e.target.value }))} />
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>
            If this email doesn't have an account yet, one is created automatically (optionally set a temporary password below; otherwise one is generated).
          </div>
          <input style={inputStyle} type="text" placeholder="Temporary password (optional)" value={addForm.password} onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))} />
          {addError && <div style={{ color: '#dc2626', fontSize: 11 }}>{addError}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={saving} onClick={handleAdd} style={{ flex: 1, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '6px 8px', fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Adding…' : 'Add Family Member/Guardian'}
            </button>
            <button type="button" disabled={saving} onClick={() => { setAdding(false); setAddError(''); }} style={{ flex: 1, background: '#f1f5f9', border: '1px solid #d1d9e6', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
